
import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import NavItems from "./Header/NavItems";
import AccountButton from "./Header/AccountButton";
import MobileNavigation from "./Header/MobileNavigation";
import { useIsMobile } from "@/hooks/use-mobile";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, [signOut, navigate]);

  return (
    <header
      className="fixed top-0 left-0 w-full z-50 transition-shadow bg-white shadow-md border-b-2 border-heaven-gold"
      style={{
        height: "82px",
        minHeight: "64px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div className="container mx-auto px-3 md:px-6 flex items-center justify-between h-full">
        <div className="flex items-center justify-center">
          <Link to="/" className="flex items-center">
            <img
              src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//selah%20logo%20horizontal.png"
              alt="Selah Logo"
              className="h-16 md:h-20 object-contain pr-2"
            />
          </Link>
        </div>
        
        {!isMobile && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <NavItems />
          </div>
        )}
        
        <div className="flex items-center gap-1">
          {/* Desktop Account/Sign in/Sign up */}
          {!isMobile && (
            <>
              {user ? (
                <AccountButton onSignOut={handleSignOut} />
              ) : (
                <>
                  <Link to="/sign-in">
                    <Button
                      variant="outline"
                      className="ml-2 border-heaven-gold/40 px-5 py-2 bg-white rounded-full shadow hover:bg-heaven-gold/10 transition font-semibold text-heaven-gold"
                    >
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/sign-up">
                    <Button
                      variant="gold"
                      className="ml-2 px-5 py-2 bg-heaven-gold text-white rounded-full shadow hover:bg-heaven-dark-gold transition font-semibold"
                    >
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </>
          )}

          {/* Mobile Menu Button - Always visible on mobile */}
          <div className="md:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-heaven-gold bg-white text-heaven-gold hover:bg-heaven-gold/10"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent className="bg-white border-heaven-gold rounded-l-xl w-[85%] pt-6">
                <div className="mb-6 flex flex-col items-center">
                  <Link to="/" onClick={() => setOpen(false)}>
                    <img
                      src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//selah%20logo%20horizontal.png"
                      className="h-16 mb-2"
                      alt="Selah Logo"
                    />
                  </Link>
                </div>
                {/* Mobile Account/Sign in/Sign up */}
                <div className="flex flex-col gap-2 mb-4 items-center">
                  {user ? (
                    <Link to="/profile" onClick={() => setOpen(false)}>
                      <Button 
                        variant="outline" 
                        className="w-32 border-heaven-gold/40 bg-white rounded-full shadow hover:bg-heaven-gold/10 text-heaven-gold font-semibold"
                        data-testid="btn-my-account-mobile"
                      >
                        My Account
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to="/sign-in" onClick={() => setOpen(false)}>
                        <Button 
                          variant="outline"
                          className="w-32 border-heaven-gold/40 bg-white rounded-full shadow hover:bg-heaven-gold/10 text-heaven-gold font-semibold"
                        >
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/sign-up" onClick={() => setOpen(false)}>
                        <Button 
                          variant="gold"
                          className="w-32 bg-heaven-gold text-white rounded-full shadow hover:bg-heaven-dark-gold font-semibold"
                        >
                          Sign Up
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
                <MobileNavigation setOpen={setOpen} handleSignOut={handleSignOut} />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
